import {
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Switch,
  Text,
  useColorMode,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react'
import React from 'react'
import { FiArrowLeft, FiGlobe, FiLogOut, FiMoon, FiSun } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { LanguageSelector } from '../components/selectors/LanguageSelector/LanguageSelector'
import { AgentContactsManager } from '../components/AgentContactsManager'
import { useAuth } from '../context/AuthContext'
import { useLanguage } from '../hooks/utility/useLanguage'
import { useUser } from '../hooks/auth/useUser'
import { useAgents } from '../hooks/agents/useAgents'

const SettingsPage: React.FC = () => {
  const navigate = useNavigate()
  const { colorMode, toggleColorMode } = useColorMode()
  const { t } = useLanguage()
  const { user, signOut } = useAuth()
  const { data: currentUser } = useUser()
  const { agents } = useAgents()

  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textColor = useColorModeValue('gray.700', 'white')
  const subtleColor = useColorModeValue('gray.500', 'gray.400')
  const hoverBg = useColorModeValue('gray.50', 'gray.700')
  const iconColor = useColorModeValue('gray.600', 'gray.300')
  const headingColor = useColorModeValue('gray.800', 'white')
  const containerBg = useColorModeValue('gray.50', 'gray.900')

  const internalAgent = agents.find(agent => agent.slug === 'internal')

  const handleBack = () => {
    navigate(-1)
  }

  const handleSignOut = () => {
    if (signOut) {
      signOut()
    }
  }

  return (
    <Container maxW='container.md' py={8} bg={containerBg} minH='100vh'>
      <VStack spacing={8} align='stretch'>
        {/* Back Button and Title */}
        <Flex justify='space-between' align='center'>
          <IconButton
            icon={<FiArrowLeft size={24} />}
            aria-label={t('common.back')}
            variant='ghost'
            size='lg'
            color={iconColor}
            onClick={handleBack}
            _hover={{ bg: hoverBg }}
          />
          <Heading size='lg' textAlign='center' flex='1' color={headingColor}>
            {t('profile.settings')}
          </Heading>
          <Box w={10} />
        </Flex>

        {/* Profile Section */}
        <VStack
          spacing={4}
          align='stretch'
          bg={cardBg}
          p={6}
          borderRadius='xl'
          borderWidth='1px'
          borderColor={borderColor}
        >
          <Flex align='center' gap={4}>
            <Avatar
              size='xl'
              name={user?.displayName || t('chat.anonymousUser')}
              src={user?.avatar}
              bg='primary'
              color='white'
            />
            <VStack align='start' spacing={1}>
              <Text fontSize='2xl' fontWeight='medium' color={textColor}>
                {user?.displayName || t('chat.anonymousUser')}
              </Text>
              <Text color={subtleColor}>{user?.email || t('profile.noEmailAvailable')}</Text>
            </VStack>
          </Flex>
        </VStack>

        {/* Appearance Section */}
        <VStack
          spacing={4}
          align='stretch'
          bg={cardBg}
          p={6}
          borderRadius='xl'
          borderWidth='1px'
          borderColor={borderColor}
        >
          <Flex align='center' gap={3}>
            {colorMode === 'light' ? <FiSun size={20} color={iconColor} /> : <FiMoon size={20} color={iconColor} />}
            <Text fontSize='lg' fontWeight='medium' color={textColor}>
              {t('profile.appearance')}
            </Text>
          </Flex>
          <FormControl display='flex' alignItems='center'>
            <FormLabel htmlFor='dark-mode' mb='0' flex='1' color={textColor}>
              {t('profile.darkMode')}
            </FormLabel>
            <Switch
              id='dark-mode'
              isChecked={colorMode === 'dark'}
              onChange={toggleColorMode}
              colorScheme='primaryScheme'
              disabled={false}
            />
          </FormControl>
        </VStack>

        {/* Language Section */}
        <VStack
          spacing={4}
          align='stretch'
          bg={cardBg}
          p={6}
          borderRadius='xl'
          borderWidth='1px'
          borderColor={borderColor}
        >
          <Flex align='center' gap={3}>
            <FiGlobe size={20} color={iconColor} />
            <Text fontSize='lg' fontWeight='medium' color={textColor}>
              {t('profile.selectLanguage')}
            </Text>
          </Flex>
          <LanguageSelector buttonSize='lg' showText variant='outline' />
        </VStack>

        {/* Superuser: Agent Contacts Manager */}
        {currentUser?.is_superuser && internalAgent && (
          <>
            <Divider borderColor={borderColor} />
            <AgentContactsManager agentId={internalAgent.id} />
          </>
        )}

        <Divider borderColor={borderColor} />

        {/* Logout Button */}
        <Button
          leftIcon={<FiLogOut size={18} />}
          variant='ghost'
          colorScheme='red'
          size='lg'
          justifyContent='start'
          onClick={handleSignOut}
          color={useColorModeValue('red.600', 'red.300')}
          _hover={{ bg: useColorModeValue('red.50', 'red.900') }}
        >
          {t('common.logout')}
        </Button>
      </VStack>
    </Container>
  )
}

export default SettingsPage
